import { analyseTraditionalPolicy } from "../../engine/calculations";

/**
 * LIC (and broadly, traditional endowment / whole-life / money-back) policy analyser.
 *
 * Differences vs the existing `insurance-irr` calculator:
 *   - `insurance-irr` asks the user to bring the maturity value themselves.
 *   - This calculator PROJECTS the maturity value from LIC's standard bonus + FAB formula
 *     (`sum_assured + bonus_per_1000 × SA/1000 × term + fab_per_1000 × SA/1000`), so members
 *     who only have the bond page in front of them (SA, premium, term) can still see IRR.
 *   - Adds a money-back branch (paid at policy-term midpoint as a % of SA) — pure endowment
 *     runs with moneyBackPct = 0.
 *   - Adds the opportunity-cost comparison: what the same annual premium would grow to at a
 *     user-specified alternative return.
 *
 * MVP scope (07-Feb-2026): no product-family preset dropdown, no auto-link to saved policies,
 * no PDF/CSV specific to this calc (the framework's Download PDF applies). Session 2 will layer
 * on Kotak presets + ULIP charge waterfall.
 */
const licPolicyAnalyzerConfig = {
  key: "lic-policy-analyzer",
  title: "LIC / Traditional Policy Analyzer",
  category: "Insurance & Protection",
  description:
    "Enter your LIC endowment / whole-life / money-back policy details — see the actual IRR, projected maturity, and how much you'd gain by investing the same premium elsewhere.",
  fields: [
    {
      key: "sumAssured",
      label: "Sum Assured",
      type: "currency",
      default: 10_00_000,
      min: 50_000,
      max: 10_00_00_00_000,
      help: "The base cover amount on your policy bond.",
    },
    {
      key: "annualPremium",
      label: "Annual premium",
      type: "currency",
      default: 50_000,
      min: 500,
      max: 5_00_00_000,
      help: "Total premium you pay per year (aggregate monthly/quarterly if applicable).",
    },
    {
      key: "premiumYears",
      label: "Premium paying term (PPT)",
      type: "number",
      default: 15,
      min: 1,
      max: 50,
      help: "Years you actually pay premium for. Sometimes shorter than policy term.",
    },
    {
      key: "policyTermYears",
      label: "Policy term",
      type: "number",
      default: 20,
      min: 5,
      max: 60,
      help: "Total years until maturity. Must be ≥ PPT.",
    },
    {
      key: "bonusPer1000",
      label: "Assumed simple reversionary bonus (₹ per ₹1000 SA per year)",
      type: "number",
      default: 45,
      min: 0,
      max: 100,
      help: "LIC endowment plans typically declare 40–50 per ₹1000 SA. Check your last bonus statement.",
    },
    {
      key: "fabPer1000",
      label: "Final Additional Bonus (₹ per ₹1000 SA, one-time at maturity)",
      type: "number",
      default: 40,
      min: 0,
      max: 200,
      help: "Only credited for policies of 15+ years. Set 0 for shorter terms or non-participating plans.",
    },
    {
      key: "moneyBackPct",
      label: "Money-back payout at midpoint (% of SA)",
      type: "percent",
      default: 0,
      min: 0,
      max: 100,
      help: "Set 20–50 for Money-Back policies (paid at policy midpoint). Keep 0 for endowment / whole-life.",
    },
    {
      key: "altReturn",
      label: "Alternative investment return (for opportunity cost)",
      type: "percent",
      default: 12,
      min: 4,
      max: 20,
      assumptionKey: "equityReturn",
      help: "The return you'd expect if the same premium went into an MF SIP instead. Typical equity long-term: 12%.",
    },
  ],
  chart: {
    type: "bar",
    xKey: "name",
    series: [{ key: "amount", label: "Amount", colorVar: "--gold" }],
  },
  compute(values) {
    const sumAssured = Number(values.sumAssured) || 0;
    const annualPremium = Number(values.annualPremium) || 0;
    const premiumYears = Number(values.premiumYears) || 0;
    const policyTermYears = Math.max(Number(values.policyTermYears) || 0, premiumYears);
    const bonusPer1000 = Number(values.bonusPer1000) || 0;
    // FAB only meaningful for 15+ year policies — auto-zero it otherwise so shorter-term
    // policies don't get a phantom bonus that misleads the IRR upward.
    const fabPer1000 = policyTermYears >= 15 ? Number(values.fabPer1000) || 0 : 0;
    const moneyBackPct = Number(values.moneyBackPct) || 0;
    const altReturn = (Number(values.altReturn) || 0) / 100;

    const analysis = analyseTraditionalPolicy({
      sumAssured,
      annualPremium,
      premiumYears,
      policyTermYears,
      bonusPer1000,
      fabPer1000,
      moneyBackPct,
      altReturn,
    });

    // Rule-based verdict — same thresholds an unbiased advisor uses. IRR under 4% is bond-like
    // (below inflation-adjusted safety), 4-6% is "poor but locked in", 6%+ is defensible for a
    // low-risk allocation.
    let verdict = "KEEP";
    let verdictNote = "IRR is reasonable for a debt-equivalent, capital-safe allocation.";
    if (analysis.irr < 0.04) {
      verdict = "CONSIDER SURRENDER";
      verdictNote = "IRR below 4% — worse than a fixed deposit. If you've paid ≥3 premiums, evaluate surrender or make it paid-up.";
    } else if (analysis.irr < 0.06) {
      verdict = "REVIEW";
      verdictNote = "IRR 4–6% — locked capital growing below inflation-adjusted returns. Compare against alternative use.";
    }

    return {
      headline: {
        label: "Actual annualised return (IRR)",
        value: analysis.irr,
        format: "percent",
      },
      extraHeadlines: [
        { label: "Projected maturity value", value: analysis.maturity.total },
        { label: "Total premiums paid", value: analysis.totalPremiumsPaid },
        {
          label: "If invested at alt return instead",
          value: analysis.altCorpusAtMaturity,
        },
        {
          label: "Opportunity gap (alt − policy)",
          value: analysis.opportunityGap,
        },
      ],
      breakdown: [
        { label: "Guaranteed component (Sum Assured)", value: analysis.maturity.guaranteed },
        { label: "Accumulated bonus", value: analysis.maturity.bonus },
        { label: "Final Additional Bonus (FAB)", value: analysis.maturity.fab },
        ...(analysis.mbYear
          ? [
              {
                label: `Money-back payout (year ${analysis.mbYear})`,
                value: analysis.mbPayout,
              },
              { label: "Final payout at maturity (net of MB)", value: analysis.finalPayout },
            ]
          : []),
        { label: "Recommendation", value: `${verdict} — ${verdictNote}`, format: "text" },
      ],
      series: [
        { name: "Premiums paid", amount: analysis.totalPremiumsPaid },
        { name: "Policy maturity", amount: analysis.maturity.total },
        { name: "Alt investment", amount: analysis.altCorpusAtMaturity },
      ],
    };
  },
};

export default licPolicyAnalyzerConfig;
